import { dispatchMiddleware, enrichContextState } from "nemuru-components";
import React from "react";
import { developOptions } from "../config/developOptions";
import { services } from "../request/services";
import { ID_DOCUMENT_VERIFICATION_STATUSES } from "../components/modules/idDocumentVerification/config";
import {
  getActionException,
  NON_CRITICAL_ACTION_EXCEPTIONS_BY_REQUEST_NAME,
} from "../utils/getActionException";
import { DocumentationRequiredMapper } from "../utils/mappers/DocumentationRequiredMapper";
import { LoanMapper } from "../utils/mappers/LoanMapper";
import { RetryVideoIdActionExceptions } from "../components/modules/kyc/KycAutomatic/shared/components/RetryElectronicId";

export const ProcessContext = React.createContext();

const initialState = {
  loan: undefined,
  loading: undefined,
  borrower: undefined,
  checkoutSession: undefined,
  loanApplications: [],
  refreshLoanListNeeded: false,
  loansInfoData: {
    refreshing: false,
    hydratedLoansList: undefined,
    operationsAmountByStatus: undefined,
  },
  processCriticalError: null,
  flowItem: null,
  lastFlowItemOK: null,
  flowItemIsKO: undefined,
  currentModuleObject: null,
  currentSection: null,
  currentSectionProgress: null,
  currentFlow: undefined,
  conditionsToBeInitialModuleInFlowChecked: undefined,
  flowLoading: {
    show: true,
    animationId: undefined,
    loadingMessage: undefined,
  },
  flowMessage: {
    id: undefined,
    fadeIn: false,
  },
  appStarted: false,
  hideSections: false,
  documentationRequired: undefined,
  stepModulesProgress: [],
  documentationSyncedAfterSigning: false,
  documentType: undefined,
  processIds: undefined,
  bankReaderWidgetUrl: undefined,
  optIns: undefined,
  syncingBorrowerDocumentation: false,
  signatureChannel: null,
  caixabank: {
    response: undefined,
    isKnownCustomer: undefined,
    isNotNeeded: undefined,
    isDocumentNumberValid: undefined,
  },
  asset: undefined,
  processRetriableError: undefined,
  pricingByLoanId: undefined,
  participant: {
    role: undefined,
    participantId: undefined,
    onboardingDetails: undefined,
    onboardingFulfilledBy: undefined,
  },
  borrowerBankReader: undefined,
  borrowerBankReaderId: undefined,
  borrowerDocumentationFile: undefined,
  paymentToken: undefined,
  paymentPlan: undefined,
  videoIdProcessPassed: undefined,
  videoId: undefined,
  idDocumentVerification: {
    status: null,
    failedAttempts: 0,
    maxFailedAttempts: 3,
    lastVerifyStatus: null,
    loading: false,
    loadingAction: null,
    error: null,
    documentType: null,
    resetId: 0,
    restartingToProcessInformation: false,
    statusFetched: false,
    extractedData: null,
    extractedDataLoading: false,
    extractedDataError: null,
  },
};

const actions = {
  SET_ID_DOCUMENT_VERIFICATION_DOCUMENT_TYPE: {
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          documentType: action.payload,
        },
      };
    },
  },
  CLEAR_ID_DOCUMENT_VERIFICATION_ERROR: {
    changeState: (state) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          error: null,
        },
      };
    },
  },
  RESET_ID_DOCUMENT_VERIFICATION_FLOW: {
    changeState: (state) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          status: null,
          lastVerifyStatus: null,
          error: null,
          loading: false,
          documentType: null,
          restartingToProcessInformation: false,
          resetId: (state.idDocumentVerification?.resetId ?? 0) + 1,
        },
      };
    },
  },
  RESTART_ID_DOCUMENT_VERIFICATION_FROM_VALIDATION: {
    changeState: (state) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          status: null,
          lastVerifyStatus: null,
          error: null,
          loading: false,
          restartingToProcessInformation: true,
          resetId: (state.idDocumentVerification?.resetId ?? 0) + 1,
          extractedData: null,
          extractedDataError: null,
        },
      };
    },
  },

  UPDATE_PROCESS_CONTEXT: {
    changeState: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
  RESET_FLOW: {
    changeState: (state, action) => {
      return {
        ...state,
        flowItem: 0,
        currentFlow: undefined,
        flowNeedsCheck: true,
        conditionsToBeInitialModuleInFlowChecked: false,
      };
    },
  },
  SET_CURRENT_FLOW: {
    changeState: (state, action) => {
      return {
        ...state,
        currentFlow: action.payload,
        flowNeedsCheck: false,
        flowItem: null,
      };
    },
  },
  START_APP: {
    changeState: (state, action) => {
      return {
        ...state,
        appStarted: true,
      };
    },
  },
  SET_FLOW_LOADING: {
    changeState: (state, action) => {
      return {
        ...state,
        flowLoading: action.payload,
      };
    },
  },
  SET_FLOW_ITEM_OK: {
    changeState: (state, action) => {
      return {
        ...state,
        lastFlowItemOK: state.flowItem,
        flowItem: state.flowItem + 1,
        flowItemIsKO: false,
      };
    },
  },
  SET_FLOW_ITEM_PREVIOUS: {
    changeState: (state, action) => {
      return {
        ...state,
        lastFlowItemOK: state.flowItem,
        flowItem: state.flowItem - 1,
        flowItemIsKO: false,
      };
    },
  },
  SET_MODULE_KO: {
    changeState: (state, action) => {
      return {
        ...state,
        flowItemIsKO: true,
      };
    },
  },
  SET_MODULE_SKIPPED: {
    changeState: (state, action) => {
      return {
        ...state,
        lastFlowItemOK: state.flowItem,
        flowItem: state.flowItem + 1,
        flowItemIsKO: false,
      };
    },
  },
  SET_SCREEN_PATH: {
    changeState: (state, action) => {
      return {
        ...state,
        screenPath: action.payload,
      };
    },
  },
  RESET_MODULE_KO: {
    changeState: (state, action) => {
      return {
        ...state,
        flowItemIsKO: false,
      };
    },
  },
  RESET_PROCESS_RETRIABLE_ERROR: {
    changeState: (state, action) => {
      return {
        ...state,
        processRetriableError: false,
        actionException: null,
      };
    },
  },
  SET_CURRENT_MODULE_OBJECT: {
    changeState: (state, action) => {
      return {
        ...state,
        currentModuleObject: action.payload,
      };
    },
  },
  SET_CURRENT_SECTION: {
    changeState: (state, action) => {
      return {
        ...state,
        currentSection: action.payload,
      };
    },
  },
  SET_CURRENT_SECTION_PROGRESS: {
    changeState: (state, action) => {
      return {
        ...state,
        currentSectionProgress: action.payload,
      };
    },
  },
  SET_FLOW_MESSAGE: {
    changeState: (state, action) => {
      return {
        ...state,
        flowMessage: action.payload,
      };
    },
  },
  SET_SECTIONS_ARRAY: {
    changeState: (state, action) => {
      return {
        ...state,
        sectionsArray: action.payload,
      };
    },
  },
  SET_PARTICIPANT: {
    changeState: (state, action) => {
      return {
        ...state,
        participant: action.payload,
      };
    },
  },
  REQUEST_GET_MERCHANT: {
    request: services.getMerchantById,
    changeState: (state, action) => {
      return { ...state, loading: true, merchant: null };
    },
  },
  [`${"REQUEST_GET_MERCHANT"}_FULFILLED`]: {
    changeState: (state, action) => {
      return { ...state, merchant: action.payload.payload, loading: false };
    },
  },
  [`${"REQUEST_GET_MERCHANT"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        merchant: null,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  PROCESS_PATCH_ASSET_DETAILS_BY_LOAN_ID: {
    request: services.patchAssetDetailsWithLoanId,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"PROCESS_PATCH_ASSET_DETAILS_BY_LOAN_ID"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"PROCESS_PATCH_ASSET_DETAILS_BY_LOAN_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  PROCESS_PATCH_SET_BORROWER_ONBOARDING_DETAILS_BY_ROLE: {
    request: services.patchAssetDetailsWithLoanId,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"PROCESS_PATCH_SET_BORROWER_ONBOARDING_DETAILS_BY_ROLE"}_FULFILLED`]: {
    changeState: (state) => {
      return { ...state, loading: false };
    },
  },
  [`${"PROCESS_PATCH_SET_BORROWER_ONBOARDING_DETAILS_BY_ROLE"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
        loading: false,
      };
    },
  },
  REQUEST_PATCH_SET_USER_DETAILS_WITH_KYC_PROVIDER_DATA: {
    request: services.patchSetUserDetailsWithKycProviderData,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_SET_USER_DETAILS_WITH_KYC_PROVIDER_DATA"}_FULFILLED`]: {
    chainAction: (action) => ({
      type: "REQUEST_GET_LOAN",
      payload: action.requestPayload.loan_id,
    }),
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_PATCH_SET_USER_DETAILS_WITH_KYC_PROVIDER_DATA"}_REJECTED`]: {
    changeState: (state, action) => {
      const exception = action.payload.data.exception;
      const actionException = getActionException(exception);
      return {
        ...state,
        loading: false,
        snackbarNotification: RetryVideoIdActionExceptions.includes(
          actionException
        )
          ? null
          : {
              snackbarInformation: action,
              level: "error",
            },
        actionException: actionException,
      };
    },
  },
  REQUEST_PATCH_SUBMIT_KYC_VERIFICATION: {
    request: services.patchSubmitKycVerification,
    changeState: (state) => ({ ...state, loading: true }),
  },
  [`${"REQUEST_PATCH_SUBMIT_KYC_VERIFICATION"}_FULFILLED`]: {
    chainAction: (action) => ({
      type: "REQUEST_GET_LOAN",
      payload: action.requestPayload.loan_id,
    }),
    changeState: (state) => state,
  },
  [`${"REQUEST_PATCH_SUBMIT_KYC_VERIFICATION"}_REJECTED`]: {
    changeState: (state, action) => {
      const exception = action.payload?.data?.exception;
      const actionException = getActionException(exception);
      return {
        ...state,
        loading: false,
        snackbarNotification: RetryVideoIdActionExceptions.includes(
          actionException
        )
          ? null
          : {
              snackbarInformation: action,
              level: "error",
            },
        actionException,
      };
    },
  },
  REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID: {
    request: services.getOptInByLoanIdAndBorrowerId,
    changeState: (state, action) => {
      return {
        ...state,
        loading: true,
        optIns: null,
      };
    },
  },
  [`${"REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        optIns: action.payload.payload,
        loading: false,
        optInsResponse: true,
      };
    },
  },
  [`${"REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        optIns: null,
        optInsResponse: true,
      };
    },
  },
  REQUEST_PATCH_OPTIN_BY_ID_CONDITIONS_CHECK_IN: {
    request: services.processPatchCheckOptinConditions,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_OPTIN_BY_ID_CONDITIONS_CHECK_IN"}_FULFILLED`]: {
    changeState: (state) => {
      return { ...state, loading: false };
    },
  },
  [`${"REQUEST_PATCH_OPTIN_BY_ID_CONDITIONS_CHECK_IN"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },

  REQUEST_PATCH_OPTIN_BY_ID_CONDITIONS_CHECK_IN_AND_SEQUENCE: {
    request: services.processPatchCheckOptinConditions,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_OPTIN_BY_ID_CONDITIONS_CHECK_IN_AND_SEQUENCE"}_FULFILLED`]:
    {
      chainAction: (action, state) => {
        return {
          type: "REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID",
          payload: {
            loanId: action.requestPayload.loanId,
            borrowerId: action.requestPayload.borrowerId,
          },
        };
      },
      changeState: (state) => {
        return state;
      },
    },
  [`${"REQUEST_PATCH_OPTIN_BY_ID_CONDITIONS_CHECK_IN_AND_SEQUENCE"}_REJECTED`]:
    {
      changeState: (state, action) => {
        return {
          ...state,
          loading: false,
          snackbarNotification: {
            snackbarInformation: action,
            level: "error",
          },
        };
      },
    },

  REQUEST_PATCH_LENDER_SPECIFIC_OPTIN_BY_ID_AND_SEQUENCE: {
    request: services.processPatchLenderSpecificOptinConditions,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_LENDER_SPECIFIC_OPTIN_BY_ID_AND_SEQUENCE"}_FULFILLED`]: {
    chainAction: (action) => ({
      type: "REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID",
      payload: {
        loanId: action.requestPayload.loanId,
        borrowerId: action.requestPayload.borrowerId,
      },
    }),
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_PATCH_LENDER_SPECIFIC_OPTIN_BY_ID_AND_SEQUENCE"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },

  REQUEST_PATCH_SYNC_LENDER_OPTINS_AND_SEQUENCE: {
    request: services.processPatchSyncLenderOptIns,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_SYNC_LENDER_OPTINS_AND_SEQUENCE"}_FULFILLED`]: {
    chainAction: (action, state) => {
      return {
        type: "REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID",
        payload: {
          loanId: action.requestPayload.loanId,
          borrowerId: action.requestPayload.borrowerId,
        },
      };
    },
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_PATCH_SYNC_LENDER_OPTINS_AND_SEQUENCE"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        processCriticalError: action,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_POST_CREATE_BORROWER_OPTIN_AND_SEQUENCE: {
    request: services.postCreateBorrowerOptin,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_CREATE_BORROWER_OPTIN_AND_SEQUENCE"}_FULFILLED`]: {
    chainAction: (action) => ({
      type: "REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID",
      payload: {
        loanId: action.requestPayload.loan_id,
        borrowerId: action.requestPayload.borrower_id,
      },
    }),
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_POST_CREATE_BORROWER_OPTIN_AND_SEQUENCE"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_POST_CREATE_BORROWER: {
    request: services.postCreateBorrower,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_CREATE_BORROWER"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_POST_CREATE_BORROWER"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_PATCH_SET_BORROWER_ID_TO_LOAN_AND_SEQUENCE: {
    request: services.patchSetBorrowerIdToLoan,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_SET_BORROWER_ID_TO_LOAN_AND_SEQUENCE"}_FULFILLED`]: {
    chainAction: (action) => ({
      type: "REQUEST_GET_LOAN",
      payload: action.requestPayload.id,
    }),
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_PATCH_SET_BORROWER_ID_TO_LOAN_AND_SEQUENCE"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_PATCH_SET_USER_DETAILS_WITH_DECLARED_DATA: {
    request: services.patchSetUserDetailsWithDeclaredData,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_SET_USER_DETAILS_WITH_DECLARED_DATA"}_FULFILLED`]: {
    chainAction: (action) => ({
      type: "REQUEST_GET_LOAN",
      payload: action.requestPayload.loan_id,
    }),
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_PATCH_SET_USER_DETAILS_WITH_DECLARED_DATA"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_PATCH_SET_PROFESSIONAL_DETAILS_AND_SEQUENCE: {
    request: services.patchSetProfessionalDetails,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_SET_PROFESSIONAL_DETAILS_AND_SEQUENCE"}_FULFILLED`]: {
    chainAction: (action) => ({
      type: "REQUEST_GET_LOAN",
      payload: action.requestPayload.loan_id,
    }),
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_PATCH_SET_PROFESSIONAL_DETAILS_AND_SEQUENCE"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_POST_DOCUMENT_ID_IMAGES_ACTION: {
    request: services.postDocumentIdImagesAction,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_DOCUMENT_ID_IMAGES_ACTION"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_POST_DOCUMENT_ID_IMAGES_ACTION"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_GET_ID_DOCUMENT_VERIFICATION_STATUS: {
    request: services.getIdDocumentVerificationStatus,
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: true,
          loadingAction: action.type,
          error: null,
        },
      };
    },
  },
  [`${"REQUEST_GET_ID_DOCUMENT_VERIFICATION_STATUS"}_FULFILLED`]: {
    changeState: (state, action) => {
      const payload = action?.payload?.payload;
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          error: null,
          statusFetched: true,
          status: payload?.status ?? null,
          failedAttempts: payload?.failed_attempts ?? 0,
          maxFailedAttempts: payload?.max_failed_attempts ?? 3,
        },
      };
    },
  },
  [`${"REQUEST_GET_ID_DOCUMENT_VERIFICATION_STATUS"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        processRetriableError: action,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          statusFetched: true,
          error: action,
        },
      };
    },
  },
  REQUEST_GET_ID_DOCUMENT_VERIFICATION_EXTRACTED_DATA: {
    request: services.getIdDocumentVerificationExtractedData,
    changeState: (state) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          extractedDataLoading: true,
          extractedDataError: null,
        },
      };
    },
  },
  [`${"REQUEST_GET_ID_DOCUMENT_VERIFICATION_EXTRACTED_DATA"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          extractedDataLoading: false,
          extractedData: action?.payload?.payload ?? action?.payload ?? null,
        },
      };
    },
  },
  [`${"REQUEST_GET_ID_DOCUMENT_VERIFICATION_EXTRACTED_DATA"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          extractedDataLoading: false,
          extractedDataError: action,
        },
      };
    },
  },
  REQUEST_PATCH_SET_USER_DETAILS_WITH_ID_DOCUMENT_VERIFICATION_FORM_DATA: {
    request:
      services.processPatchSetUserDetailsWithIdDocumentVerificationFormData,
    changeState: (state) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_SET_USER_DETAILS_WITH_ID_DOCUMENT_VERIFICATION_FORM_DATA"}_FULFILLED`]:
    {
      chainAction: (action) => ({
        type: "REQUEST_GET_LOAN",
        payload: action.requestPayload.loan_id,
      }),
      changeState: (state) => {
        return state;
      },
    },
  [`${"REQUEST_PATCH_SET_USER_DETAILS_WITH_ID_DOCUMENT_VERIFICATION_FORM_DATA"}_REJECTED`]:
    {
      changeState: (state, action) => {
        return {
          ...state,
          loading: false,
          snackbarNotification: {
            snackbarInformation: action,
            level: "error",
          },
        };
      },
    },
  REQUEST_POST_ID_DOCUMENT_VERIFICATION_START: {
    request: services.postIdDocumentVerificationStart,
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: true,
          loadingAction: action.type,
          error: null,
        },
      };
    },
  },
  [`${"REQUEST_POST_ID_DOCUMENT_VERIFICATION_START"}_FULFILLED`]: {
    changeState: (state) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          error: null,
          status: ID_DOCUMENT_VERIFICATION_STATUSES.PENDING,
          restartingToProcessInformation: false,
        },
      };
    },
  },
  [`${"REQUEST_POST_ID_DOCUMENT_VERIFICATION_START"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          error: action,
        },
      };
    },
  },

  REQUEST_PUT_ID_DOCUMENT_VERIFICATION_FRONT: {
    request: services.putIdDocumentVerificationFront,
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: true,
          loadingAction: action.type,
          error: null,
        },
      };
    },
  },
  [`${"REQUEST_PUT_ID_DOCUMENT_VERIFICATION_FRONT"}_FULFILLED`]: {
    changeState: (state) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          error: null,
          status: ID_DOCUMENT_VERIFICATION_STATUSES.PENDING,
        },
      };
    },
  },
  [`${"REQUEST_PUT_ID_DOCUMENT_VERIFICATION_FRONT"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          error: action,
          restartingToProcessInformation: true,
        },
      };
    },
  },

  REQUEST_PUT_ID_DOCUMENT_VERIFICATION_BACK: {
    request: services.putIdDocumentVerificationBack,
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: true,
          loadingAction: action.type,
          error: null,
        },
      };
    },
  },
  [`${"REQUEST_PUT_ID_DOCUMENT_VERIFICATION_BACK"}_FULFILLED`]: {
    changeState: (state) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          error: null,
          status: ID_DOCUMENT_VERIFICATION_STATUSES.PENDING,
        },
      };
    },
  },
  [`${"REQUEST_PUT_ID_DOCUMENT_VERIFICATION_BACK"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          error: action,
          restartingToProcessInformation: true,
        },
      };
    },
  },

  REQUEST_POST_ID_DOCUMENT_VERIFICATION_VERIFY: {
    request: services.postIdDocumentVerificationVerify,
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: true,
          loadingAction: action.type,
          error: null,
        },
      };
    },
  },
  [`${"REQUEST_POST_ID_DOCUMENT_VERIFICATION_VERIFY"}_FULFILLED`]: {
    changeState: (state, action) => {
      const payload = action?.payload?.payload;
      const status = payload?.status ?? null;
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          error: null,
          status,
          lastVerifyStatus: status,
          failedAttempts:
            payload?.failed_attempts ??
            state.idDocumentVerification.failedAttempts,
          maxFailedAttempts:
            payload?.max_failed_attempts ??
            state.idDocumentVerification.maxFailedAttempts,
        },
      };
    },
  },
  [`${"REQUEST_POST_ID_DOCUMENT_VERIFICATION_VERIFY"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        idDocumentVerification: {
          ...state.idDocumentVerification,
          loading: false,
          loadingAction: null,
          error: action,
        },
      };
    },
  },

  REQUEST_GET_DECLARED_INCOME: {
    request: services.getDeclaredIncomeById,
    changeState: (state, action) => {
      return { ...state, loading: true, income: null };
    },
  },
  [`${"REQUEST_GET_DECLARED_INCOME"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        income: action.payload.payload,
      };
    },
  },
  [`${"REQUEST_GET_DECLARED_INCOME"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        income: {},
      };
    },
  },
  REQUEST_POST_DECLARED_INCOME: {
    request: services.postDeclaredIncome,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_DECLARED_INCOME"}_FULFILLED`]: {
    changeState: (state, action) => {
      return { ...state, loading: false };
    },
  },
  [`${"REQUEST_POST_DECLARED_INCOME"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_PATCH_ADD_BORROWER_CASH_IN_CASH_OUT_METHOD: {
    request: services.patchAddBorrowerCashInCashOutMethod,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_ADD_BORROWER_CASH_IN_CASH_OUT_METHOD"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_PATCH_ADD_BORROWER_CASH_IN_CASH_OUT_METHOD"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },

  REQUEST_PATCH_SET_BORROWER_CASH_IN_CASH_OUT_METHOD_TO_LOAN_AND_SEQUENCE: {
    request: services.patchSetBorrowerCashInCashOutMethodToLoan,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_SET_BORROWER_CASH_IN_CASH_OUT_METHOD_TO_LOAN_AND_SEQUENCE"}_FULFILLED`]:
    {
      chainAction: (action) => ({
        type: "REQUEST_GET_LOAN",
        payload: action.requestPayload.id,
      }),
      changeState: (state) => {
        return state;
      },
    },
  [`${"REQUEST_PATCH_SET_BORROWER_CASH_IN_CASH_OUT_METHOD_TO_LOAN_AND_SEQUENCE"}_REJECTED`]:
    {
      changeState: (state, action) => {
        return {
          ...state,
          loading: false,
          snackbarNotification: {
            snackbarInformation: action,
            level: "error",
          },
        };
      },
    },

  REQUEST_GET_SIGNATURE_URL_BY_LOAN_ID: {
    request: services.getSignatureUrlByLoanId,
    changeState: (state, action) => {
      return { ...state, loading: true, signatureChannel: null };
    },
  },
  [`${"REQUEST_GET_SIGNATURE_URL_BY_LOAN_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        signatureChannel: {
          requestedAt: Date.now(),
          ...action.payload.payload,
        },
      };
    },
  },
  [`${"REQUEST_GET_SIGNATURE_URL_BY_LOAN_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      const exception = action.payload.data.exception;
      const actionException = getActionException(exception);

      const isActionExceptionNotCritical =
        NON_CRITICAL_ACTION_EXCEPTIONS_BY_REQUEST_NAME.REQUEST_GET_SIGNATURE_URL_BY_LOAN_ID.some(
          (exception) => exception === actionException
        );
      return {
        ...state,
        loading: false,
        signatureChannel: null,
        processRetriableError: isActionExceptionNotCritical ? null : action,
        actionException: isActionExceptionNotCritical ? actionException : null,
      };
    },
  },

  REQUEST_GET_SIGNATURE_PARAMS_BY_LOAN_ID: {
    request: services.getSignatureParamsByLoanId,
    changeState: (state, action) => {
      return { ...state, loading: true, signatureChannel: null };
    },
  },
  [`${"REQUEST_GET_SIGNATURE_PARAMS_BY_LOAN_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        signatureChannel: {
          requestedAt: Date.now(),
          ...action.payload.payload,
        },
      };
    },
  },
  [`${"REQUEST_GET_SIGNATURE_PARAMS_BY_LOAN_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      const exception = action.payload.data.exception;
      const actionException = getActionException(exception);
      const isActionExceptionNotCritical =
        NON_CRITICAL_ACTION_EXCEPTIONS_BY_REQUEST_NAME.REQUEST_GET_SIGNATURE_PARAMS_BY_LOAN_ID_REJECTED.some(
          (exception) => exception === actionException
        );
      return {
        ...state,
        loading: false,
        signatureChannel: null,
        // processRetriableError: isActionExceptionNotCritical ? false : action, // TODO => review if this key is needed when caixabank response 500 is solved.
        actionException: isActionExceptionNotCritical ? actionException : null,
      };
    },
  },

  REQUEST_PATCH_UPDATE_SIGNATURE_STATUS_FOR_LOAN_LENDER_BORROWER_CONTRACT: {
    request:
      services.processPatchUpdateSignatureStatusForLoanLenderBorrowerContract,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_UPDATE_SIGNATURE_STATUS_FOR_LOAN_LENDER_BORROWER_CONTRACT"}_FULFILLED`]:
    {
      changeState: (state, action) => {
        return {
          ...state,
          loading: false,
        };
      },
    },
  [`${"REQUEST_PATCH_UPDATE_SIGNATURE_STATUS_FOR_LOAN_LENDER_BORROWER_CONTRACT"}_REJECTED`]:
    {
      changeState: (state, action) => {
        return {
          ...state,
          loading: false,
          processCriticalError: action,
        };
      },
    },
  REQUEST_GET_DOCUMENTATION_REQUIRED: {
    request: services.getDocumentationRequired,
    changeState: (state, action) => {
      return {
        ...state,
        loading: true,
        documentationRequired: null,
      };
    },
  },
  [`${"REQUEST_GET_DOCUMENTATION_REQUIRED"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        documentationRequired: DocumentationRequiredMapper.hydrate(
          action.payload.payload
        ),
        editableDocumentationRequired:
          DocumentationRequiredMapper.filterEditableRequiredDocuments(
            action.payload.payload
          ),
      };
    },
  },
  [`${"REQUEST_GET_DOCUMENTATION_REQUIRED"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        documentationRequired: {},
      };
    },
  },
  REQUEST_GET_BORROWER_DOCUMENTATION_FILE_BY_LOAN_ID_BORROWER_ID_AND_FILE_TYPE:
    {
      request: services.getBorrowerFileByLoanIdBorrowerIdAndFileType,
      changeState: (state, action) => {
        return { ...state, loading: true };
      },
    },
  [`${"REQUEST_GET_BORROWER_DOCUMENTATION_FILE_BY_LOAN_ID_BORROWER_ID_AND_FILE_TYPE"}_FULFILLED`]:
    {
      changeState: (state, action) => {
        return { ...state, borrowerDocumentationFile: action.payload.data };
      },
    },
  [`${"REQUEST_GET_BORROWER_DOCUMENTATION_FILE_BY_LOAN_ID_BORROWER_ID_AND_FILE_TYPE"}_REJECTED`]:
    {
      changeState: (state, action) => {
        return {
          ...state,
          loading: false,
          snackbarNotification: {
            snackbarInformation: action,
            level: "error",
          },
        };
      },
    },
  REQUEST_UPLOAD_BORROWER_DOCUMENTATION: {
    request: services.uploadBorrowerDocumentation,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_UPLOAD_BORROWER_DOCUMENTATION"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_UPLOAD_BORROWER_DOCUMENTATION"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_PATCH_SET_DOCUMENTS_CONFIRMED: {
    request: services.patchSetDocumentsConfirmed,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_SET_DOCUMENTS_CONFIRMED"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_PATCH_SET_DOCUMENTS_CONFIRMED"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_GET_LOAN: {
    request: services.getLoan,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_LOAN"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        loan: LoanMapper.hydrate(action.payload.payload),
      };
    },
  },
  [`${"REQUEST_GET_LOAN"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        processCriticalError: action,
      };
    },
  },
  REQUEST_GET_PRICING_BY_LOAN_ID: {
    request: services.getPricingByLoanId,
    changeState: (state) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_PRICING_BY_LOAN_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        pricingByLoanId: action.payload.payload,
      };
    },
  },
  [`${"REQUEST_GET_PRICING_BY_LOAN_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        processCriticalError: action,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_GET_AVAILABLE_LOAN_CALCULATORS: {
    request: services.getAvailableLoanCalculators,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_AVAILABLE_LOAN_CALCULATORS"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        loanCalculator: action.payload.data,
      };
    },
  },
  [`${"REQUEST_GET_AVAILABLE_LOAN_CALCULATORS"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        processCriticalError: action,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_GET_AVAILABLE_LOAN_CALCULATORS_BY_PRINCIPAL_AMOUNT: {
    request: services.getLoanAvailableCalculatorsByPrincipalAmount,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_AVAILABLE_LOAN_CALCULATORS_BY_PRINCIPAL_AMOUNT"}_FULFILLED`]:
    {
      changeState: (state, action) => {
        return {
          ...state,
          loading: false,
          loanCalculator: action.payload.data,
        };
      },
    },
  [`${"REQUEST_GET_AVAILABLE_LOAN_CALCULATORS_BY_PRINCIPAL_AMOUNT"}_REJECTED`]:
    {
      changeState: (state, action) => {
        return {
          ...state,
          loading: false,
          processCriticalError: action,
          snackbarNotification: {
            snackbarInformation: action,
            level: "error",
          },
        };
      },
    },
  REQUEST_PATCH_SET_LOAN_CALCULATOR: {
    request: services.patchSetLoanCalculator,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_PATCH_SET_LOAN_CALCULATOR"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_PATCH_SET_LOAN_CALCULATOR"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_GET_LENDER_IDS_BY_MERCHANT_ID: {
    request: services.getLenderIdsByMerchantId,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_LENDER_IDS_BY_MERCHANT_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        lenderIdsByMerchantId: action.payload.data,
      };
    },
  },
  [`${"REQUEST_GET_LENDER_IDS_BY_MERCHANT_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        processCriticalError: action,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_POST_LOAN_APPLICATIONS: {
    request: services.postLoanApplications,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_LOAN_APPLICATIONS"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_POST_LOAN_APPLICATIONS"}_REJECTED`]: {
    changeState: (state, action) => {
      const exception = action.payload.data.exception;
      const actionException = getActionException(exception);
      const isActionExceptionNotCritical =
        NON_CRITICAL_ACTION_EXCEPTIONS_BY_REQUEST_NAME.REQUEST_POST_LOAN_APPLICATIONS_REJECTED.some(
          (exception) => exception === actionException
        );
      return {
        ...state,
        loading: false,
        processCriticalError: isActionExceptionNotCritical ? false : action,
        actionException: isActionExceptionNotCritical ? actionException : null,
        snackbarNotification: !isActionExceptionNotCritical && {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  GET_KYC_AUTH: {
    request: services.getKycAuth,
    changeState: (state, action) => {
      return { ...state, loading: true, kycAuthorization: null };
    },
  },
  [`${"GET_KYC_AUTH"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        kycAuthorization: action.payload.authorization,
      };
    },
  },
  // EUROBITS
  REQUEST_GET_PROCESS_IDS: {
    request: services.getProcessIdsEurobits,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_PROCESS_IDS"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        processIds: action.payload.data,
      };
    },
  },
  [`${"REQUEST_GET_PROCESS_IDS"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_GET_BANK_READER_WIDGET_URL_EUROBITS: {
    request: services.getBankReaderWidgetUrlEurobits,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_BANK_READER_WIDGET_URL_EUROBITS"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        bankReaderWidgetUrl: action.payload.url,
      };
    },
  },
  [`${"REQUEST_GET_BANK_READER_WIDGET_URL_EUROBITS"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_POST_ADD_CASH_IN_CASH_OUT_METHOD_EUROBITS: {
    request: services.postAddCashInCashOutMethodEurobits,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_ADD_CASH_IN_CASH_OUT_METHOD_EUROBITS"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_POST_ADD_CASH_IN_CASH_OUT_METHOD_EUROBITS"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  // TINK
  REQUEST_GET_BANK_READER_WIDGET_URL_TINK: {
    request: services.getBankReaderWidgetUrlTink,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_BANK_READER_WIDGET_URL_TINK"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        bankReaderWidgetUrl: action.payload.url,
      };
    },
  },
  [`${"REQUEST_GET_BANK_READER_WIDGET_URL_TINK"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_POST_CALLBACK_BORROWER_BANK_READER_TINK: {
    request: services.postCallbackBorrowerBankReaderTink,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_CALLBACK_BORROWER_BANK_READER_TINK"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_POST_CALLBACK_BORROWER_BANK_READER_TINK"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_POST_ADD_CASH_IN_CASH_OUT_METHOD_TINK: {
    request: services.postAddCashInCashOutMethodTink,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_ADD_CASH_IN_CASH_OUT_METHOD_TINK"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_POST_ADD_CASH_IN_CASH_OUT_METHOD_TINK"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_GET_BORROWER_BANK_READER_BY_ID: {
    request: services.getBorrowerBankReaderById,
    changeState: (state, action) => {
      return { ...state, loading: true, borrowerBankReader: undefined };
    },
  },
  [`${"REQUEST_GET_BORROWER_BANK_READER_BY_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        borrowerBankReader: action.payload.payload,
      };
    },
  },
  [`${"REQUEST_GET_BORROWER_BANK_READER_BY_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        borrowerBankReader: undefined,
      };
    },
  },
  REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID: {
    request: services.patchSyncBorrowerDocumentationByLoanId,
    changeState: (state, action) => {
      return {
        ...state,
        loading: true,
        syncingBorrowerDocumentation: true,
      };
    },
  },
  [`${"REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        syncingBorrowerDocumentation: false,
      };
    },
  },
  [`${"REQUEST_PATCH_SYNC_BORROWER_DOCUMENTATION_BY_LOAN_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_GET_CAIXABANK_KYC: {
    request: services.getCaixabankKyc,
    changeState: (state, action) => {
      return {
        ...state,
        caixabank: { ...state.caixabank, response: false },
        loading: true,
      };
    },
  },
  [`${"REQUEST_GET_CAIXABANK_KYC"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        caixabank: {
          ...state.caixabank,
          isKnownCustomer: action.payload.payload.is_know_customer,
          response: true,
        },
      };
    },
  },
  [`${"REQUEST_GET_CAIXABANK_KYC"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        caixabank: { ...state.caixabank, response: true },
      };
    },
  },
  REQUEST_GET_CAIXABANK_KYC_PROJECTION: {
    request: services.getCaixabankKycProjection,
    changeState: (state, action) => {
      return {
        ...state,
        caixabank: { ...state.caixabank, response: false },
        loading: true,
      };
    },
  },
  [`${"REQUEST_GET_CAIXABANK_KYC_PROJECTION"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        caixabank: {
          ...state.caixabank,
          isKnownCustomer: action.payload.payload.is_known_customer,
          response: true,
        },
      };
    },
  },
  [`${"REQUEST_GET_CAIXABANK_KYC_PROJECTION"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        caixabank: { ...state.caixabank, response: true },
      };
    },
  },
  REQUEST_GET_ASSET_DETAILS: {
    request: services.getAssetDetails,
    changeState: (state, action) => {
      return {
        ...state,
        loading: true,
      };
    },
  },
  [`${"REQUEST_GET_ASSET_DETAILS"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        asset: action.payload.payload,
      };
    },
  },
  [`${"REQUEST_GET_ASSET_DETAILS"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_GET_TOKEN_BY_PAYMENT_LINE_ID: {
    request: services.getPaymentTokenByPaymentLineId,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_TOKEN_BY_PAYMENT_LINE_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        paymentToken: action.payload.payload.payment_token,
      };
    },
  },
  [`${"REQUEST_GET_TOKEN_BY_PAYMENT_LINE_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_GET_PAYMENT_PLAN_BY_PAYMENT_LINE_ID: {
    request: services.getPaymentPlanByPaymentLineId,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_PAYMENT_PLAN_BY_PAYMENT_LINE_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        paymentPlan: action.payload.payload,
      };
    },
  },
  [`${"REQUEST_GET_PAYMENT_PLAN_BY_PAYMENT_LINE_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_PATCH_UPDATE_PAYMENT_PLAN_LINE_STATUS: {
    request: services.patchUpdatePaymentPlanLineStatus,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  REQUEST_PATCH_UPDATE_PAYMENT_PLAN_LINE_STATUS_FULFILLED: {
    changeState: (state, action) => {
      return { ...state, loading: false };
    },
  },
  REQUEST_PATCH_UPDATE_PAYMENT_PLAN_LINE_STATUS_REJECTED: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_POST_SET_DIGITAL_FOOTPRINT: {
    request: services.postSetDigitalFootprint,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_SET_DIGITAL_FOOTPRINT"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_POST_SET_DIGITAL_FOOTPRINT"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_PATCH_UPDATE_LOAN_CONDITIONS_BY_INSURANCE: {
    request: services.patchUpdateLoanConditionsByInsurance,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  REQUEST_PATCH_UPDATE_LOAN_CONDITIONS_BY_INSURANCE_FULFILLED: {
    changeState: (state, action) => {
      return { ...state, loading: false };
    },
  },
  REQUEST_PATCH_UPDATE_LOAN_CONDITIONS_BY_INSURANCE_REJECTED: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
  REQUEST_PATCH_SET_BORROWER_ID_TO_BORROWER_ONBOARDING_DETAILS_BY_EMAIL_AND_PHONE:
    {
      request:
        services.patchSetBorrowerIdToBorrowerOnboardingDetailsByEmailAndPhone,
      changeState: (state, action) => {
        return { ...state, loading: true };
      },
    },
  REQUEST_PATCH_SET_BORROWER_ID_TO_BORROWER_ONBOARDING_DETAILS_BY_EMAIL_AND_PHONE_FULFILLED:
    {
      changeState: (state, action) => {
        return { ...state, loading: false };
      },
    },
  REQUEST_PATCH_SET_BORROWER_ID_TO_BORROWER_ONBOARDING_DETAILS_BY_EMAIL_AND_PHONE_REJECTED:
    {
      changeState: (state, action) => {
        return {
          ...state,
          loading: false,
          snackbarNotification: {
            snackbarInformation: action,
            level: "error",
          },
        };
      },
    },
  REQUEST_GET_CHECKOUT_SESSION_BY_PAYMENT_METHOD_ID: {
    request: services.getCheckoutSessionByPaymentMethodId,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_GET_CHECKOUT_SESSION_BY_PAYMENT_METHOD_ID"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        checkoutSession: action.payload.payload,
      };
    },
  },
  [`${"REQUEST_GET_CHECKOUT_SESSION_BY_PAYMENT_METHOD_ID"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        checkoutSession: null,
        loading: false,
      };
    },
  },
  REQUEST_POST_MANUAL_CHOOSE: {
    request: services.postManualChoose,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  [`${"REQUEST_POST_MANUAL_CHOOSE"}_FULFILLED`]: {
    changeState: (state) => {
      return state;
    },
  },
  [`${"REQUEST_POST_MANUAL_CHOOSE"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
      };
    },
  },
  REQUEST_GET_LOAN_APPLICATIONS: {
    request: services.getLoanApplicationsByLoanId,
    changeState: (state, action) => {
      return {
        ...state,
        loading: true,
      };
    },
  },
  [`${"REQUEST_GET_LOAN_APPLICATIONS"}_FULFILLED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loanApplications: action.payload.data.map((data) => data.payload),
        loading: false,
      };
    },
  },
  [`${"REQUEST_GET_LOAN_APPLICATIONS"}_REJECTED`]: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        loanApplications: [],
      };
    },
  },
  REQUEST_POST_REQUEST_BORROWER_CALL: {
    request: services.postRequestBorrowerCall,
    changeState: (state, action) => {
      return { ...state, loading: true };
    },
  },
  REQUEST_POST_REQUEST_BORROWER_CALL_FULFILLED: {
    changeState: (state, action) => {
      return { ...state, loading: false };
    },
  },
  REQUEST_POST_REQUEST_BORROWER_CALL_REJECTED: {
    changeState: (state, action) => {
      return {
        ...state,
        loading: false,
        snackbarNotification: {
          snackbarInformation: action,
          level: "error",
        },
      };
    },
  },
};

function reducer(state, action) {
  // return actions[action.type].changeState(state, action);

  const originalState = actions[action.type].changeState(state, action);
  return enrichContextState(actions[action.type], originalState, action);
}

export default function ProcessContextProvider(props) {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const middlewareDispatch = dispatchMiddleware(
    dispatch,
    actions,
    developOptions
  );
  const value = { state, dispatch: middlewareDispatch };

  return (
    <ProcessContext.Provider value={value}>
      {props.children}
    </ProcessContext.Provider>
  );
}
